<template>
  <Modal v-model="visible" :title="$t('key94')" width="1100" @on-cancel="handleCancel">
    <Form ref="form" :model="formData" :rules="rules">
      <div v-for="(flow, flowIndex) in _flows" :key="flow.FlowId" class="flow-section">
        <div class="flow-content">
          <div class="flow-content-left">
            <div class="flow-header">
              <span class="flow-title">{{ $t('key95') }}：{{ flow.FlowName }}</span>
            </div>
            <!-- 左侧：试剂信息表格 -->
            <div class="materials-table">
              <Table :columns="columns" :data="getMaterialsByFlow(flow.FlowId)" size="small" stripe />
            </div>
          </div>
          <!-- 右侧：审批步骤 -->
          <div class="approval-steps">
            <div v-for="(link, linkIndex) in flow.FlowLinks" :key="link.FlowLinkId" class="step-item">
              <span class="step-label">{{ $t('key96') }}{{ linkIndex + 1 }}：</span>

              <FormItem :prop="`flows.${flowIndex}.FlowLinks.${linkIndex}.selectedApprovers`" :rules="getStepRules(link.IsOptional)">
                <Select
                  v-if="link.IsOptional === 1"
                  v-model="formData.flows[flowIndex].FlowLinks[linkIndex].selectedApprovers"
                  multiple
                  :placeholder="$t('key97')"
                  clearable
                >
                  <Option v-for="user in link.ApproveUsers" :key="user.ApproveUserId" :value="user.ApproveUserId">
                    {{ user.ApproveUser }}
                  </Option>
                </Select>
                <div v-else class="disabled-approvers">
                  <span v-for="(user, idx) in link.ApproveUsers" :key="idx">
                    {{ user.ApproveUser }}
                    <span v-if="idx < link.ApproveUsers.length - 1">、</span>
                  </span>
                </div>
              </FormItem>
            </div>
          </div>
        </div>
      </div>
    </Form>

    <template slot="footer">
      <Button type="text" @click="handleCancel">{{ $t('btn.cancel') }}</Button>
      <Button type="primary" @click="handleSubmit">{{ $t('btn.defaultConfirm') }}</Button>
    </template>
  </Modal>
</template>

<script>
  import columns from '@/utils/columns';

  export default {
    name: 'SelectApproversModal',

    props: {
      value: {
        type: Boolean,
        default: false,
      },
      flows: {
        type: Array,
        default: () => [],
      },
      materials: {
        type: Object,
        default: () => {},
      },
      // 领用； 申购
      type: {
        type: String,
        default: 'request', // request 领用；applicant： 申购
      },
    },
    data() {
      return {
        _flows: [],
        visible: this.value,
        formData: {
          flows: [],
        },
        rules: {},
        materialsColumns: [
          columns.Barcode(this.$i18n.t('columns.barcode1'), 150),
          columns.chinName(this.$i18n.t('columns.materialName'), 150),
          columns.WarehouseName(this.$i18n.t('page.warehouse')),
          columns.BottleType(this.$i18n.t('columns.bottleType')),
          columns.Purity(this.$i18n.t('columns.bOrp')),
        ],
        applicantColumns: [
          columns.ChinName(this.$i18n.t('columns.bottleName')),
          columns.Purity(this.$i18n.t('columns.bOrp')),
          columns.Specifications(this.$i18n.t('columns.bottleType')),
          columns.Price(this.$i18n.t('columns.price')),
          columns.number(this.$i18n.t('columns.number')),
          columns.TotalPrice(this.$i18n.t('columns.totalPrice')),
          columns.ProjectCode(this.$i18n.t('columns.projectCode')),
        ],
      };
    },
    computed: {
      columns() {
        return this.type === 'request' ? this.materialsColumns : this.applicantColumns;
      },
    },
    watch: {
      flows: {
        handler(newFlows) {
          const _newFlows = this.setUniqueFlows(newFlows);
          this.initializeFormData(_newFlows);
          this._flows = _newFlows;
        },
        deep: true,
        immediate: true,
      },
      value(newVal) {
        this.visible = newVal;
      },
    },
    methods: {
      // 根据flowId去重，并且过滤掉没有可选审批人的流程
      setUniqueFlows(flows) {
        const uniqueFlows = [];
        const flowIds = new Set();
        flows.forEach(flow => {
          if (!flowIds.has(flow.FlowId)) {
            flowIds.add(flow.FlowId);
            uniqueFlows.push(flow);
          }
        });
        return uniqueFlows.filter(flow => flow.HasOptional === 1);
      },
      initializeFormData(flows) {
        this.formData.flows = flows.map(flow => ({
          ...flow,
          FlowLinks: flow.FlowLinks.map(link => ({
            ...link,
            selectedApprovers: [],
          })),
        }));
      },
      getMaterialsByFlow(flowId) {
        return this.materials[flowId] || [];
      },
      getStepRules(isOptional) {
        if (isOptional === 1) {
          return [
            {
              type: 'array',
              required: true,
              message: this.$t('key98'),
              trigger: 'change',
              min: 1,
            },
          ];
        }
        return [];
      },
      async handleSubmit() {
        const valid = await this.$refs.form.validate();
        if (valid) {
          const optionalFlowLinks = [];
          this.formData.flows.forEach(flow => {
            flow.FlowLinks.forEach(link => {
              if (link.IsOptional === 1 && link.selectedApprovers.length > 0) {
                optionalFlowLinks.push({
                  RowIds: flow.RowIds,
                  FlowLinkId: link.FlowLinkId,
                  ApproveUsers: link.selectedApprovers,
                });
              }
            });
          });

          this.$emit('submit', {
            OptionalFlowLinks: optionalFlowLinks,
          });
          this.visible = false;
        }
      },
      handleCancel() {
        this.$refs.form.resetFields();
        this.visible = false;
        this.$emit('input', false);
      },
    },
  };
</script>

<style lang="less" scoped>
  .flow-section {
    margin-bottom: 30px;
    border-top: 1px solid #e8eaed;
    border-bottom: 1px solid #e8eaed;
    border-radius: 4px;

    .flow-header {
      .flow-title {
        font-weight: bold;
        font-size: 14px;
        color: #1a1a1a;
      }
    }

    .flow-content {
      display: flex;
      gap: 24px;

      .flow-content-left {
        padding: 16px 16px 16px 0;
        flex: 1;
        border-right: 1px solid #e8eaed;
      }
      .materials-table {
        padding: 12px 0;
        flex: 1;
        min-width: 440px;

        :deep(.ivu-table) {
          font-size: 12px;
          background-color: transparent;
        }
      }

      .approval-steps {
        flex: 0 0 350px;
        padding-top: 45px;

        .step-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 20px;
          gap: 8px;

          .step-label {
            flex-shrink: 0;
            min-width: 50px;
            font-size: 12px;
            color: #666;
            padding-top: 4px;
          }

          :deep(.ivu-form-item) {
            margin-bottom: 0;
            flex: 1;

            .ivu-form-item-content {
              line-height: 1.5;
            }
          }

          .disabled-approvers {
            display: inline-block;
            padding: 4px 8px;
            background-color: #f5f5f5;
            border: 1px solid #d9d9d9;
            border-radius: 2px;
            font-size: 12px;
            color: #999;
            width: 100%;
          }
        }
      }
    }
  }
</style>
